// InvoiceGroupManager.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from 'src/redux/hooks'; // Adjust if needed
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Box, Typography, Button, Stack, Paper,
  Tooltip,
  Fab,
  CircularProgress,
} from '@mui/material';
import { IInvoiceGroupAddressOverrides, IInvoiceGroupOverrides, IInvoiceGroups, ISubscriptions } from '@llws/lift-entity-interfaces';
import { compactArray, Nullable } from '@jamesgmarks/utilities';
import { IClientDropdownItem } from 'src/entity-interfaces/IClientDropdownItem';
import { getMany } from 'src/redux/features/dynamic/actions';
import { Between, Equal, GreaterThanOrEqual, Null, Or } from '@llws/dynamic-router-utils';
import { ClientDropdown } from 'src/components/parts/ClientDropdown';
import { shallowEqual, useDispatch } from 'react-redux';
import InvoiceGroupCard from 'src/components/pages/InvoiceGroups/InvoiceGroupCard';
import SplitFieldModal, { splitOptions } from 'src/components/pages/InvoiceGroups/SplitFieldModal';
import UnassignedSubscriptionsPanel from 'src/components/pages/InvoiceGroups/UnassignedSubscriptionPanel';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { loadInvoiceManagementPreview, saveInvoiceGroup } from 'src/redux/features/invoice-management/actions';
import { InvoicePreview } from 'src/components/pages/InvoiceGroups/InvoicePreview';
import { DeleteGroupModal } from 'src/components/pages/InvoiceGroups/DeleteGroupModal';
import PreviewIcon from '@mui/icons-material/Preview'; // Add this import for the icon
import { buildInvoiceGroupCommandPayload } from 'src/components/pages/InvoiceGroups/invoiceGroupSave';
import AddressOverrideModal from 'src/components/pages/InvoiceGroups/AddressOverrideModal';
import { getMonthStart, makeYmd } from 'src/app-utils';
import { clearPreviewData } from 'src/redux/features/invoice-management/invoiceManagementSlice';

export type EditableFieldKey = keyof Pick<
  IInvoiceGroups,
  'splitInvoicesBy' | 'groupLinesBy' | 'lineItemFields' | 'lineDescriptionFields'
>;
const InvoiceGroupManager = () => {
  const invoiceGroups = useAppSelector(state => state.dynamic.data.invoice_groups?.list ?? [], shallowEqual);
  const invoiceGroupsLoadState = useAppSelector(state => state.dynamic.data.invoice_groups?.loadedState);
  const subscriptions = useAppSelector(state => state.dynamic.data.subscriptions?.list ?? [], shallowEqual);
  const subscriptionsLoadState = useAppSelector(state => state.dynamic.data.subscriptions?.loadedState);
  const previewData = useAppSelector((store) => store.invoiceManagement.previewData);
  const loadedState = useAppSelector((store) => store.invoiceManagement.loadedState);
  const saveState = useAppSelector((store) => store.invoiceManagement.invoiceGroupSaveState);

  // Data management states
  const [selectedClient, setSelectedClient] = useState<Nullable<IClientDropdownItem>>(null);
  const [editableGroups, setEditableGroups] = useState<IInvoiceGroups[]>([]);
  const [editableOverrides, setEditableOverrides] = useState<IInvoiceGroupOverrides[]>([]);
  const [editableAddressOverrides, setEditableAddressOverrides] = useState<IInvoiceGroupAddressOverrides[]>([]);
  const [overridesToDelete, setOverridesToDelete] = useState<Set<number>>(new Set());
  const [groupIdsToDelete, setGroupIdsToDelete] = useState<Set<number>>(new Set());
  const [addressOverridesToDelete, setAddressOverridesToDelete] = useState<Set<number>>(new Set());
  const [modifiedGroupIds, setModifiedGroupIds] = useState<Set<number>>(new Set());
  const [modifiedOverrideIds, setModifiedOverrideIds] = useState<Set<number>>(new Set());
  const [modifiedAddressOverrideIds, setModifiedAddressOverrideIds] = useState<Set<number>>(new Set());
  const [tempIdCounter, setTempIdCounter] = useState(-1); // Helps link overrides and address overrides to the correct group

  // Modals
  const [openSplitOptionModal, setOpenSplitOptionModal] = useState(false);
  const [openSubscriptionPanel, setOpenSubscriptionPanel] = useState<boolean>(false);
  const [openAddressModal, setOpenAddressModal] = useState<boolean>(false);

  // UI management states
  const [selectedField, setSelectedField] = useState<Nullable<EditableFieldKey>>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<Nullable<number>>(null);
  const [justAddedGroupId, setJustAddedGroupId] = useState<Nullable<number>>(null);
  const [panelPosition, setPanelPosition] = useState({ x: 100, y: 10 });
  const [groupToDelete, setGroupToDelete] = useState<Nullable<number>>(null);
  const [reloadGroups, setReloadGroups] = useState<boolean>(false);
  const [pageHasErrors, setPageHasErrors] = useState<boolean>(false);

  const newGroupRef = useRef<Nullable<HTMLDivElement>>(null);
  const unassignedFabRef = useRef<HTMLButtonElement | null>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!selectedClient) return;
    const currentDate = new Date();
    const currentMonthStart = getMonthStart(currentDate.getFullYear(), currentDate.getMonth() + 1);

    getMany('invoice_groups', {
      alias: 'ig',
      relations: [
        { path: 'ig.invoiceGroupOverrides', alias: 'igo' },
        { path: 'ig.invoiceGroupAddressOverrides', alias: 'igao' },
      ],
      where: [
        Equal('ig.freshbooksClientId', selectedClient.freshbooksClientId),
      ],
      limit: 'false',
    });

    getMany('subscriptions', {
      alias: 's',
      where: [
        Equal('s.clientId', selectedClient.id),
        {
          condition: 'and',
          whereCommands: [
            Or(GreaterThanOrEqual('s.expiryDate', makeYmd(currentMonthStart, true))),
            Or(Null('s.expiryDate')),
          ],
        },
      ],
      limit: 'false',
    });
    setReloadGroups(false);

  }, [selectedClient, reloadGroups]);

  useEffect(() => {
    // Create copy of invoice groups
    setEditableGroups(invoiceGroups.map(g => ({ ...g })));

    // Create copy of all invoice group overrides
    const allOverrides = invoiceGroups.flatMap(g => g.invoiceGroupOverrides || []);
    setEditableOverrides(allOverrides);

    // Create copy of all invoice group address overrides
    const allAddressOverrides = compactArray(invoiceGroups.map(g => g.invoiceGroupAddressOverrides));
    setEditableAddressOverrides(allAddressOverrides);

    // Clear all the data tracked information
    setModifiedGroupIds(new Set());
    setModifiedOverrideIds(new Set());
    setModifiedAddressOverrideIds(new Set());
    setGroupIdsToDelete(new Set());
    setOverridesToDelete(new Set());
    setAddressOverridesToDelete(new Set());
    setTempIdCounter(-1);

    // Clear UI tracked info
    setJustAddedGroupId(null);

    // Clear preview
    dispatch(clearPreviewData());
  }, [invoiceGroups, dispatch]);

  useEffect(() => {
    // To scroll to a newly added group
    if (justAddedGroupId && newGroupRef.current) {
      newGroupRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setJustAddedGroupId(null);
    }
  }, [editableGroups, justAddedGroupId]);

  const saveDataAvailable = useMemo(() => (
    groupIdsToDelete.size > 0 ||
    overridesToDelete.size > 0 ||
    addressOverridesToDelete.size > 0 ||
    modifiedGroupIds.size > 0 ||
    modifiedOverrideIds.size > 0 ||
    modifiedAddressOverrideIds.size > 0
  ), [
    groupIdsToDelete,
    overridesToDelete,
    addressOverridesToDelete,
    modifiedGroupIds,
    modifiedOverrideIds,
    modifiedAddressOverrideIds,
  ]);

  const handleAssignSubscription = (groupId: number, subscriptionId: number) => {
    const invoiceGroup = editableGroups.find((ig) => ig.id === groupId);
    const oldOverrideId = editableOverrides.find(o => o.subscriptionId === subscriptionId)?.id;

    setEditableOverrides(prev => {
      const filtered = prev.filter(o => o.subscriptionId !== subscriptionId);

      const newOverride: IInvoiceGroupOverrides = {
        invoiceGroupId: groupId,
        subscriptionId,
        groupLinesBy: invoiceGroup?.groupLinesBy,
        lineItemFields: invoiceGroup?.lineItemFields,
        lineDescriptionFields: invoiceGroup?.lineDescriptionFields,
      };

      return [...filtered, newOverride];
    });

    setModifiedOverrideIds(prev => {
      const updated = new Set(prev);
      updated.add(subscriptionId);
      return updated;
    });

    if (typeof oldOverrideId === 'number') {
      setOverridesToDelete(prev => {
        const updated = new Set(prev);
        updated.add(oldOverrideId);
        return updated;
      });
    }
  };

  const handleUnassignSubscription = (subscriptionId: number) => {
    const overrideToRemove = editableOverrides.find(o => o.subscriptionId === subscriptionId);
    if (overrideToRemove) {
      setEditableOverrides(prev => prev.filter(o => o.subscriptionId !== subscriptionId));
      const id = overrideToRemove.id;
      if (typeof id === 'number') {
        setOverridesToDelete(prev => {
          const updated = new Set(prev);
          updated.add(id);
          return updated;
        });
      }
      setModifiedOverrideIds(prev => {
        const updated = new Set(prev);
        updated.delete(subscriptionId);
        return updated;
      });
    }
  };

  // Update the field for all overrides assigned to the invoice group
  const handleOverrideFieldUpdate = (groupId: number, fieldKey: EditableFieldKey, newValue: string) => {
    setEditableOverrides(prev =>
      prev.map(o =>
        o.invoiceGroupId === groupId
          ? { ...o, [fieldKey]: newValue }
          : o));
    setModifiedOverrideIds(prev => {
      // Mark all affected overrides as modified
      const updated = new Set(prev);
      editableOverrides.forEach(o => {
        if (o.invoiceGroupId === groupId && typeof o.id === 'number') {
          updated.add(o.subscriptionId);
        }
      });
      return updated;
    });
  };

  const handleAddressOverrideSave = (addressOverride: IInvoiceGroupAddressOverrides, groupId: number) => {
    const addressOverrideDetails = {
      ...addressOverride,
      invoiceGroupId: groupId,
    };
    const idx = editableAddressOverrides.findIndex(
      (ao) => ao.invoiceGroupId === addressOverrideDetails.invoiceGroupId,
    );
    const prevValue = idx !== -1 ? editableAddressOverrides[idx] : null;
    const prevValueId = prevValue?.id;
    setEditableAddressOverrides(prev => {
      if (prevValue) {
        // Overwrite existing
        const updated = [...prev];
        updated[idx] = { id: prevValueId, ...addressOverrideDetails };
        return updated;
      }
      // Add new
      return [...prev, addressOverrideDetails];
    });
    setModifiedAddressOverrideIds(prev => {
      const updated = new Set(prev);
      updated.add(groupId);
      return updated;
    });

  };

  const handleAddNewGroup = () => {
    const newId = tempIdCounter;
    const newGroup: IInvoiceGroups = {
      id: newId,
      freshbooksClientId: selectedClient!.freshbooksClientId,
      splitInvoicesBy: null,
      groupLinesBy: 'subscription_id',
      lineItemFields: 'invoice_item',
      lineDescriptionFields: 'invoice_description',
      invoiceGroupOverrides: [],
      invoiceGroupAddressOverrides: { invoiceGroupId: newId, useClientAddress: 1 },
    };

    setEditableGroups((prev) => [...prev, newGroup]);
    setModifiedGroupIds((prev) => new Set(prev).add(newId));
    setJustAddedGroupId(newId);
    setTempIdCounter((prev) => prev - 1);
  };

  const handleDeleteGroup = (groupId: number) => {
    const overrideIdsToDelete = compactArray(
      editableOverrides.filter(
        (o) => (o.invoiceGroupId === groupId),
      ).map((o) => ({ id: o.id, subscriptionId: o.subscriptionId })),
    ); const addressOverrideToDelete = editableGroups.find(
      (eg) => eg.id === groupId,
    )?.invoiceGroupAddressOverrides;

    // Remove from draft Data
    setEditableGroups(prev => prev.filter(g => g.id !== groupId));
    setEditableOverrides(prev => prev.filter(o => o.invoiceGroupId !== groupId));
    setEditableAddressOverrides(prev => prev.filter(ao => ao.invoiceGroupId !== groupId));

    if (groupId > 0) { //checks if it an unsaved group or a group that exists already
      // 1- Add group id to be deleted
      setGroupIdsToDelete(prev => {
        const updated = new Set(prev);
        updated.add(groupId);
        return updated;
      });

      // 2- Add all associated overrides to be deleted
      setOverridesToDelete(prev => {
        const updated = new Set(prev);
        for (const override of overrideIdsToDelete) {
          if (override.id)
            updated.add(override.id);
        }
        return updated;
      });

      // 3- if an address override exists, Add
      if (addressOverrideToDelete) {
        const addressOverrideId = addressOverrideToDelete.id;
        addressOverrideId && setAddressOverridesToDelete(prev => {
          const updated = new Set(prev);
          updated.add(addressOverrideId);
          return updated;
        });
      }
    }

    // Make sure that none of the three objects are being tracked for the group id anymore
    setModifiedGroupIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(groupId);
      return newSet;
    });

    setModifiedOverrideIds(prev => {
      const updated = new Set(prev);
      for (const override of overrideIdsToDelete) {
        updated.delete(override.subscriptionId);
      }
      return updated;
    });

    if (addressOverrideToDelete) {
      setModifiedAddressOverrideIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(addressOverrideToDelete.invoiceGroupId);
        return newSet;
      });
    }
  };

  const handleSave = async () => {
    if (!selectedClient || !selectedClient.billingAccountId) {
      return;
    }
    // Only include groups that have been modified (added or edited)
    const filteredEditableGroups = editableGroups.filter(g => typeof g.id === 'number' && modifiedGroupIds.has(g.id));

    // Only include overrides that have been modified (added or edited)
    const filteredEditableOverrides = editableOverrides.filter(
      o => typeof o.subscriptionId === 'number' && modifiedOverrideIds.has(o.subscriptionId),
    );

    const filteredEditableAddressOverrides = editableAddressOverrides.filter(
      ao => typeof ao.invoiceGroupId === 'number' && modifiedAddressOverrideIds.has(ao.invoiceGroupId),
    );

    const payload = buildInvoiceGroupCommandPayload(
      invoiceGroups,
      filteredEditableGroups,
      filteredEditableOverrides,
      filteredEditableAddressOverrides,
      groupIdsToDelete,
      overridesToDelete,
      addressOverridesToDelete,
    );
    const saved = await saveInvoiceGroup(payload, selectedClient.billingAccountId);

    if (saved) { setReloadGroups(true); }
  };

  const assignedSubscriptionIds = editableOverrides.map(o => o.subscriptionId);
  const unassignedSubscriptions = subscriptions.filter(sub => !assignedSubscriptionIds.includes(sub.id!));

  return (
    <DndProvider backend={HTML5Backend}>
      {
        !!(selectedClient && openSubscriptionPanel)
        && <UnassignedSubscriptionsPanel
          subscriptions={unassignedSubscriptions}
          position={panelPosition}
          subscriptionLoadState={subscriptionsLoadState}
          onClose={() => setOpenSubscriptionPanel(false)}
          onUnassign={(subscriptionId: number) => handleUnassignSubscription(subscriptionId)}
        />
      }
      <Box p={2}>
        <Box mb={4}>
          <Typography variant="h5" align="center" gutterBottom>
            Invoice Group Management
          </Typography>
        </Box>

        <Box mb={4} display="flex" justifyContent="center">
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              width: '100%',
              maxWidth: "80vh",
              bgcolor: '#fafafa',
            }}
          >
            <Stack spacing={2}>
              <ClientDropdown
                clientId={selectedClient?.id ?? null}
                onClientChanged={(client) => setSelectedClient(client)}
              />
              {selectedClient && (
                <Stack direction="row" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1} >
                    <Typography fontWeight="bold">
                      Client Name:
                    </Typography>
                    <Typography variant="body1"> {selectedClient.name}</Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography fontWeight="bold">
                      Account ID:
                    </Typography>
                    <Typography variant="body1">{selectedClient.freshbooksClientId}</Typography>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Paper>
        </Box>

        {
          selectedClient
          && <Box display="flex" flexDirection="column" alignItems="center" mt={3}>
            <Typography
              variant="h6"
              color="warning"
              mb={2}
              sx={{
                fontWeight: 'bold',
                textAlign: 'center',
                backgroundColor: '#fff8e1',
                border: '2px solid #ffa000',
                borderRadius: '8px',
                padding: '12px',
                boxShadow: 3,
              }}
            >
              ⚠️ Save your changes to the invoice groups and reload the preview to see the updated effects!
            </Typography>
          </Box>
        }

        <>
          {invoiceGroupsLoadState === 'loading'
            ? <Stack direction="row" justifyContent="center" alignItems="center" mt={3}>
              <CircularProgress />
            </Stack>

            : <>
              {
                selectedClient
                && <Stack direction="row" justifyContent={"center"} flexWrap="wrap" mb={4}>
                  {selectedField && selectedGroupId !== null && (
                    (() => {
                      const group = editableGroups.find(g => g.id === selectedGroupId);
                      const value: string = (group?.[selectedField]) ?? '';

                      return (
                        <SplitFieldModal
                          open={openSplitOptionModal}
                          fieldKey={selectedField}
                          groupId={selectedGroupId}
                          options={splitOptions[selectedField]}
                          initialValue={value}
                          onUpdate={(newValue) => {
                            setEditableGroups(prev =>
                              prev.map(g =>
                                g.id === selectedGroupId ? { ...g, [selectedField]: newValue } : g));
                            setModifiedGroupIds(prev => new Set(prev).add(selectedGroupId));
                            handleOverrideFieldUpdate(selectedGroupId, selectedField, newValue);
                          }}
                          onClose={() => setOpenSplitOptionModal(false)}
                        />
                      );
                    })()
                  )}
                  {editableGroups.map(group => (
                    <Box
                      key={group.id}
                      ref={group.id === justAddedGroupId ? newGroupRef : null}
                      sx={{
                        mr: 1,
                        my: 1,
                      }}
                    >
                      <InvoiceGroupCard
                        key={group.id}
                        group={group}
                        overrides={editableOverrides.filter(o => o.invoiceGroupId === group.id)}
                        subscriptions={subscriptions}
                        subscriptionLoadState={subscriptionsLoadState}
                        onAssign={handleAssignSubscription}
                        onEditField={(groupId, fieldKey) => {
                          setSelectedGroupId(groupId);
                          setSelectedField(fieldKey);
                          setOpenSplitOptionModal(true);
                        }}
                        onDelete={(id) => setGroupToDelete(id)}
                        onAddressEdit={(id) => {
                          setOpenAddressModal(true);
                          setSelectedGroupId(id);
                        }}
                        onErrorChange={(groupId, hasErrors) => setPageHasErrors(hasErrors)}
                      />
                    </Box>
                  ))}
                </Stack>
              }
              {
                selectedClient
                && <>
                  <DeleteGroupModal
                    groupToDelete={groupToDelete}
                    onClose={() => setGroupToDelete(null)}
                    onDelete={(groupId) => {
                      if (groupToDelete != null) {
                        handleDeleteGroup(groupToDelete);
                        setGroupToDelete(null);
                      }
                    }}
                  />
                  <AddressOverrideModal
                    open={openAddressModal}
                    selectedGroupId={selectedGroupId}
                    clientName={selectedClient?.name ?? null}
                    initialOverride={
                      editableGroups.find((ig) => ig.id === selectedGroupId)?.invoiceGroupAddressOverrides ?? null
                    }
                    onSave={(val, groupId) => handleAddressOverrideSave(val, groupId)}
                    onClose={() => {
                      setOpenAddressModal(false);
                      setSelectedGroupId(null);
                    }}
                  />
                </>
              }
            </>
          }
        </>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: 'fixed',
            top: 84,
            right: 24,
            zIndex: 4000,
          }}
        >
          <Tooltip title="Add New Invoice Group" arrow>
            <span>
              <Fab
                color="primary"
                aria-label="add"
                onClick={handleAddNewGroup}
                disabled={!selectedClient}
              >
                <AddIcon />
              </Fab>
            </span>
          </Tooltip>
          <Tooltip title="Show Unassigned Subs" arrow>
            <span>
              <Fab
                ref={unassignedFabRef}
                color="primary"
                aria-label="open panel"
                onClick={() => {
                  if (unassignedFabRef.current) {
                    const rect = unassignedFabRef.current.getBoundingClientRect();
                    setPanelPosition({
                      x: panelPosition.x,
                      y: rect.bottom + window.scrollY - 100, // -100 is just to offset the position so it is closer to the top
                    });
                  }
                  setOpenSubscriptionPanel(!openSubscriptionPanel);
                }}
                disabled={!selectedClient}
              >
                <ExpandMoreIcon />
              </Fab>
            </span>
          </Tooltip>
          <Tooltip title="Preview Invoices" arrow>
            <span>
              <Fab
                color="secondary"
                aria-label="preview"
                onClick={() => selectedClient && loadInvoiceManagementPreview(selectedClient.freshbooksClientId)}
                disabled={loadedState === 'loading' || !selectedClient}
              >
                <PreviewIcon />
              </Fab>
            </span>
          </Tooltip>
          <Tooltip title="Save Changes" arrow>
            <span>
              <Fab
                color="success"
                aria-label="save"
                onClick={handleSave}
                disabled={!saveDataAvailable || pageHasErrors || saveState === 'saving'}
              >
                <SaveIcon />
              </Fab>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {
        loadedState === 'loading' && (
          <Stack direction="row" justifyContent="center" alignItems="center" mt={3}>
            <CircularProgress />
          </Stack>
        )
      }
      {
        selectedClient && previewData && loadedState === 'loaded' && (
          <Stack spacing={3} mt={3} mx={8}>
            {previewData.map((preview, index) => (
              <InvoicePreview preview={preview} index={index} key={index} />
            ))}
          </Stack>
        )
      }
    </DndProvider >
  );
};

export default InvoiceGroupManager;
